import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import PresentPC from 'components/pages/Station/PresentPC';
import PresentSP from 'components/pages/Station/PresentSP';

import {
  CHOICES_MAP,
  SUCCESS_MESSAGES,
  CUSTOM_ERROR_MESSAGES,
  DROPDOWN_INITIAL_ITEM,
  MEASURE_MAP,
} from 'utils/constant';
import { ekiSuggestApi } from 'utils/api';
import {
  isSchemaValid, 
  isCustomValid, 
  useStationParamCheck,
  useWindowDimensions,
  isFatalApiError,
  sortErrors,
  JbmErrorMessage,
  useIECheck,
  MoveCheckStart,
  postHeightToParent,
} from 'utils';
import MeasureEvent from 'utils/measureEvent';

import AppContext from 'fluxes/context';
import { UPDATE_STATION, CHANGE_VALID } from 'fluxes/actions';
import queryString from 'query-string';

const Station = () => {
  const path = window.location.pathname;
  const { state, dispatch } = useContext(AppContext);
  const { lpmode } = queryString.parse( useLocation().search );
  const { referrer } = document;
  const stage = process.env.REACT_APP_STAGE || 'local';
  const [ modalBackIsEnable, setModalBackIsEnable ] = useState(false);
  const [ searchNames, setSearchNames ] = useState([]);
  const [ selectedEkiCd, setSelectedEkiCd ] = useState(state.ekiCd);
  const [ ekiName, setEkiName ] = useState(state.ekiName);
  const [ ekiCd, setEkiCd ] = useState(state.ekiCd);
  const [ moveTypeCd, setMoveTypeCd ] = useState(state.moveTypeCd);
  const [ moveTime, setMoveTime ] = useState(state.moveTime);
  const [ moveTimeFromBusStop, setMoveTimeFromBusStop ] = useState(state.moveTimeFromBusStop);
  const [ successMessage, setSuccessMessage ] = useState('');
  const [ lpMode, setLpMode ] = useState(state.lpMode);
  const [ lpID, setLpID ] = useState(state.lpID);
  const [ lpOrigin, setLpOrigin ] = useState(state.lpOrigin);
  const pageEl = useRef(null);

  // 入力チェック用変数
  const initialValid = { valid: state.validMap[path], errorMessages: [] };
  const [ valids, setValids ] = useState({
    schema: initialValid,
    ekiSuggestZero: initialValid,
    checkEkiCode: initialValid,
  });
  const [ isShowError, setIsShowError ] = useState({
    ekiName: false,
    ekiCd: false,
    moveTime: false,
    moveTimeFromBusStop: false,
  });
  const [ isApiError, setIsApiError ] = useState(false);

  // エラーの画面表示で利用する値
  const valid = Object.keys(valids).every(key => valids[key].valid);
  const dispErrors = useCallback(() => {
    const { schema, ekiSuggestZero, checkEkiCode } = valids;
    const errors = sortErrors([
      ...schema.errorMessages,
      ...ekiSuggestZero.errorMessages,
      ...checkEkiCode.errorMessages,
    ]);
    const errorMap = errors.reduce((obj, error) => {
      const { target, message } = error;
      if(!isShowError[target] || Object.keys(obj).includes(target)) return obj;
      return { ...obj, [target]: message };
    }, {});
    console.log({ errorMap });
    return errorMap;
  }, [ isShowError, valids ]);

  const { width } = useWindowDimensions();
  const isIE = useIECheck();
  const isSp = width <= 640 && !isIE;
  let { moveTypeCdList } = CHOICES_MAP;
  const [ timerId, setTimerId ] = useState('');
  

  // プルダウン両対応用
  const forHandleChange = clickVal => {
    return isSp ? clickVal.target.value : clickVal; 
  };
  
  // SP用にダミーを削除
  const changeSpList = (list) => {
    return list.filter(item => item.value !== DROPDOWN_INITIAL_ITEM.value);
  };

  if(isSp) {
    moveTypeCdList = changeSpList(moveTypeCdList);
  }

  // LP埋め込み時のパラメータ設定
  if(lpmode && !lpMode) {
    setLpMode(true);
    setLpID(lpmode);
    if(referrer.match(/^(https:\/\/)?[^/]+.r-staffing.co.jp(\/|$)/g) || stage === 'develop' || stage === 'local'){
      setLpOrigin(referrer);
    }
  };

  // 直接遷移チェック
  useStationParamCheck({ state });
  MoveCheckStart();
  
  // 入力チェック（全体）
  useEffect(()=> {
    // エラーチェックを実行
    let [ isValid, newErrorMessages ] = isSchemaValid({
      target: {
        ekiName,
        selectedEkiCd,
        moveTypeCd,
        moveTime,
        searchNames,
      },
    });
    if(moveTypeCd === '2'){
      [ isValid, newErrorMessages ] = isSchemaValid({
        target: {
          ekiName,
          selectedEkiCd,
          moveTypeCd,
          moveTime,
          searchNames,
          moveTimeFromBusStop,
        },
      });
    }
    console.log('valid:schema', { isValid, newErrorMessages });

    // erros更新
    const newValid = { schema: { valid: isValid, errorMessages: newErrorMessages } };
    setValids(org => ({ ...org, ...newValid }));

  }, [ ekiName, moveTime, moveTimeFromBusStop, moveTypeCd, searchNames, selectedEkiCd ]);

  // 駅コードチェック
  useEffect(() => {
    const validation = async () => {
      // エラーチェックを実行
      const [ isValid, newErrorMessages ] = await isCustomValid({
        checks: [ 'checkEkiCode' ],
        target: { ekiCd },
      })
        .catch(err => {
          console.log('valid:custom:checkEkiCode：err');
          // FATELエラーチェック
          const isFatal = isFatalApiError(err);
          setIsApiError(isFatal);
          if(isFatal) throw err;
        });
      console.log('valid:custom:checkEkiCode', { isValid, newErrorMessages });
      
      // OK時メッセージ
      if(ekiCd !== '' && isValid){
        setSuccessMessage(SUCCESS_MESSAGES.checkEkiCodeOK);
        setSelectedEkiCd(ekiCd);
      };
      if(ekiCd !== ''){
        // イベント計測
        MeasureEvent(
          {
            linkTrackVars: MEASURE_MAP.EVENT_EKI_SELECT.linkTrackVars,
            prop37: `jbmClientStation:${ ekiName }:${ isValid ? 'OK' : 'NG' }`,
            eVar37: MEASURE_MAP.EVENT_EKI_SELECT.eVar37,
          },
          MEASURE_MAP.EVENT_EKI_SELECT.name,
        );
      }

      
      // erros更新
      const newValid = { checkEkiCode: { valid: isValid, errorMessages: newErrorMessages } };
      setValids(org => ({ ...org, ...newValid }));
    };

    validation();

  }, [ ekiCd ]);

  // LP埋め込み時の高さ送信
  useEffect(() => {
    if(lpMode){
      postHeightToParent(lpOrigin, pageEl.current.clientHeight);
    }
  }, [ lpMode, lpOrigin, pageEl, width, moveTypeCd ]);

  // 駅サジェスト表示タイマー
  const ekiSuggestTimer = async (searchText) => {
    const id = setTimeout(() => {
      ekiSuggestApi({ searchText }).then((value)=> {
        console.log(value);
        setSearchNames(value);
        // 駅サジェストあり
        if(value.length > 0) {
          const newValid = {
            ekiSuggestZero: {
              valid: true,
              errorMessages: [],
            },
          };
          setModalBackIsEnable(true);
          setValids(org => ({ ...org, ...newValid }));
        }else{
          setModalBackIsEnable(false);
        }
      }).catch(err => {
        setModalBackIsEnable(false);
        // FATELエラーチェック
        const isFatal = isFatalApiError(err);
        setIsApiError(isFatal);
        if(isFatal) throw err;
        // 駅サジェスト0件
        if(err.response.status === 404){
          setSearchNames([]);
          // erros更新
          const newValid = {
            ekiSuggestZero: {
              valid: false,
              errorMessages: [ new JbmErrorMessage('ekiName', CUSTOM_ERROR_MESSAGES.ekiSuggestZero, 1) ],
            },
          };
          setValids(org => ({ ...org, ...newValid }));
        } 
        return [];
      });
      // ekiNameエラー表示開始
      if(!isShowError.ekiName) {
        setIsShowError({
          ...isShowError,
          ...{
            ekiName: true,
            ekiCd: true,
          },
        });
      }
    }, 200, searchText);
    return id;
  };

  // 駅名入力時
  const handleChangeEkiName = async (e) => {

    console.log(`nativeEvent.inputType: ${e.nativeEvent.inputType}`);
    // safariのdeleteCompositionTextを無視
    if(e.nativeEvent.inputType === 'deleteCompositionText' || e.nativeEvent.inputType === 'insertFromComposition'){
      return;
    }
    
    const searchText = e.target.value;
    setEkiName(searchText);
    setEkiCd('');
    setSelectedEkiCd('');
    setSuccessMessage('');

    // 先行タイマー停止
    clearTimeout(timerId);
    // タイマー設定
    setTimerId(await ekiSuggestTimer(searchText));   
  };

  // 駅サジェストクリック時
  const handleClickEkiSuggest = async ( idx ) => {
    const clicked = searchNames[idx];
    setEkiName(clicked.ekiNm);
    setEkiCd(clicked.ekiCd);
    setSearchNames([]);
    setModalBackIsEnable(false);
  };

  // 交通手段変更時
  const handleChangeMoveTypeCd = ( clickVal ) =>{
    setMoveTypeCd(forHandleChange(clickVal));
  };

  // 交通時間変更時
  const handleChangeMoveTime = ( e ) =>{
    setMoveTime(e.target.value);
    // moveTimeエラー表示開始
    if(!isShowError.moveTime) {
      setIsShowError({ ...isShowError, moveTime: true });
    }
  };
  // バス停からの交通時間変更時
  const handleChangeMoveTimeFromBusStop = ( e ) =>{
    setMoveTimeFromBusStop(e.target.value);
    // moveTimeFromBusStopエラー表示開始
    if(!isShowError.moveTimeFromBusStop) {
      setIsShowError({ ...isShowError, moveTimeFromBusStop: true });
    }
  };  
  
  // ボタン押下
  // state更新処理
  const updateState = () => {
    dispatch({
      type: UPDATE_STATION, 
      updStation: { 
        ekiName,
        ekiCd: selectedEkiCd,
        moveTypeCd,
        moveTime,
        moveTimeFromBusStop,
        lpMode,
        lpID,
      },
    });
  };
  
  const onClickMainButton = {
    next: () => {
      updateState();
      dispatch({
        type: CHANGE_VALID, 
        path,
        valid,
      });
    },
  };

  return(
    <div id='pageStation' ref={pageEl}>
      { isSp ? (
        <PresentSP 
          onClickMainButton={onClickMainButton}
          handleChangeEkiName={handleChangeEkiName}
          handleClickEkiSuggest={handleClickEkiSuggest}
          modalBackIsEnable={modalBackIsEnable}
          handleChangeMoveTypeCd={handleChangeMoveTypeCd}
          handleChangeMoveTime={handleChangeMoveTime}
          handleChangeMoveTimeFromBusStop={handleChangeMoveTimeFromBusStop}
          moveTypeCdList={moveTypeCdList}
          ekiName={ekiName}
          searchNames={searchNames}
          moveTypeCd={moveTypeCd}
          moveTime={moveTime}
          moveTimeFromBusStop={moveTimeFromBusStop}
          valid={valid}
          errorDispMap={dispErrors()}
          successMessage={successMessage}
          isApiError={isApiError}
          lpMode={lpMode}
        />
      ) : (
        <PresentPC 
          onClickMainButton={onClickMainButton}
          handleChangeEkiName={handleChangeEkiName}
          modalBackIsEnable={modalBackIsEnable}
          handleClickEkiSuggest={handleClickEkiSuggest}
          handleChangeMoveTypeCd={handleChangeMoveTypeCd}
          handleChangeMoveTime={handleChangeMoveTime}
          handleChangeMoveTimeFromBusStop={handleChangeMoveTimeFromBusStop}
          moveTypeCdList={moveTypeCdList}
          ekiName={ekiName}
          searchNames={searchNames}
          moveTypeCd={moveTypeCd}
          moveTime={moveTime}
          moveTimeFromBusStop={moveTimeFromBusStop}
          valid={valid}
          errorDispMap={dispErrors()}
          successMessage={successMessage}
          isApiError={isApiError}
          lpMode={lpMode}
        />
      )}
    </div>
  );
};

export default Station;